import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { nanoid } from 'nanoid';

export interface HabitGoal {
  id: string;
  name: string;
  targetDays: number;
  currentStreak: number;
  bestStreak: number;
  completedDays: number;
  totalDays: number;
  category: string;
  createdAt: string;
  lastUpdated: string;
  lastSaved: string;
  isActive: boolean;
  reminderTime?: string;
  lastCompleted?: string;
  suggestedTarget?: number;
  history: Array<{
    date: string;
    completed: boolean;
  }>;
}

interface HabitState {
  habits: HabitGoal[];
  addHabit: (habit: Omit<HabitGoal, 'id' | 'createdAt' | 'lastUpdated' | 'lastSaved' | 'history'>) => void;
  updateHabit: (id: string, updates: Partial<HabitGoal>) => void;
  toggleHabitCompletion: (id: string, date: string) => void;
  toggleHabitActive: (id: string) => void;
  saveHabits: () => Promise<void>;
}

// Generate realistic history data
const generateHistory = (days: number, successRate: number) => {
  return Array.from({ length: days }, (_, i) => ({
    date: new Date(Date.now() - (days - 1 - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    completed: Math.random() < successRate
  }));
};

// Initial habits data with 11+ habits
const initialHabits: HabitGoal[] = [
  {
    id: nanoid(),
    name: 'Morning Sunlight',
    targetDays: 7,
    currentStreak: 5,
    bestStreak: 12,
    completedDays: 25,
    totalDays: 30,
    category: 'circadian',
    createdAt: '2024-01-01T00:00:00.000Z',
    lastUpdated: new Date().toISOString(),
    lastSaved: new Date().toISOString(),
    isActive: true,
    reminderTime: '07:00',
    history: generateHistory(30, 0.7)
  },
  {
    id: nanoid(),
    name: 'Evening Sunset',
    targetDays: 7,
    currentStreak: 3,
    bestStreak: 8,
    completedDays: 18,
    totalDays: 30,
    category: 'circadian',
    createdAt: '2024-01-01T00:00:00.000Z',
    lastUpdated: new Date().toISOString(),
    lastSaved: new Date().toISOString(),
    isActive: true,
    reminderTime: '17:30',
    history: generateHistory(30, 0.6)
  },
  {
    id: nanoid(),
    name: 'No Late Meals',
    targetDays: 7,
    currentStreak: 2,
    bestStreak: 10,
    completedDays: 20,
    totalDays: 30,
    category: 'nutrition',
    createdAt: '2024-01-01T00:00:00.000Z',
    lastUpdated: new Date().toISOString(),
    lastSaved: new Date().toISOString(),
    isActive: true,
    reminderTime: '18:00',
    history: generateHistory(30, 0.65)
  },
  {
    id: nanoid(),
    name: 'Caffeine Cutoff',
    targetDays: 7,
    currentStreak: 4,
    bestStreak: 14,
    completedDays: 22,
    totalDays: 30,
    category: 'nutrition',
    createdAt: '2024-01-01T00:00:00.000Z',
    lastUpdated: new Date().toISOString(),
    lastSaved: new Date().toISOString(),
    isActive: true,
    reminderTime: '14:00',
    history: generateHistory(30, 0.7)
  },
  {
    id: nanoid(),
    name: 'Sleep Mask',
    targetDays: 7,
    currentStreak: 6,
    bestStreak: 16,
    completedDays: 26,
    totalDays: 30,
    category: 'sleep',
    createdAt: '2024-01-01T00:00:00.000Z',
    lastUpdated: new Date().toISOString(),
    lastSaved: new Date().toISOString(),
    isActive: true,
    reminderTime: '21:00',
    history: generateHistory(30, 0.75)
  },
  {
    id: nanoid(),
    name: 'Same Bed Time',
    targetDays: 7,
    currentStreak: 3,
    bestStreak: 9,
    completedDays: 19,
    totalDays: 30,
    category: 'sleep',
    createdAt: '2024-01-01T00:00:00.000Z',
    lastUpdated: new Date().toISOString(),
    lastSaved: new Date().toISOString(),
    isActive: true,
    reminderTime: '21:30',
    history: generateHistory(30, 0.6)
  },
  {
    id: nanoid(),
    name: 'Meditation',
    targetDays: 7,
    currentStreak: 8,
    bestStreak: 18,
    completedDays: 24,
    totalDays: 30,
    category: 'recovery',
    createdAt: '2024-01-01T00:00:00.000Z',
    lastUpdated: new Date().toISOString(),
    lastSaved: new Date().toISOString(),
    isActive: true,
    reminderTime: '08:00',
    history: generateHistory(30, 0.75)
  },
  {
    id: nanoid(),
    name: 'Cold Exposure',
    targetDays: 5,
    currentStreak: 4,
    bestStreak: 11,
    completedDays: 21,
    totalDays: 30,
    category: 'recovery',
    createdAt: '2024-01-01T00:00:00.000Z',
    lastUpdated: new Date().toISOString(),
    lastSaved: new Date().toISOString(),
    isActive: true,
    reminderTime: '06:30',
    history: generateHistory(30, 0.65)
  },
  {
    id: nanoid(),
    name: 'Supplements',
    targetDays: 7,
    currentStreak: 3,
    bestStreak: 7,
    completedDays: 16,
    totalDays: 30,
    category: 'supplements',
    createdAt: '2024-01-01T00:00:00.000Z',
    lastUpdated: new Date().toISOString(),
    lastSaved: new Date().toISOString(),
    isActive: true,
    reminderTime: '08:00',
    history: generateHistory(30, 0.55)
  },
  {
    id: nanoid(),
    name: 'Hydration',
    targetDays: 7,
    currentStreak: 2,
    bestStreak: 5,
    completedDays: 14,
    totalDays: 30,
    category: 'nutrition',
    createdAt: '2024-01-01T00:00:00.000Z',
    lastUpdated: new Date().toISOString(),
    lastSaved: new Date().toISOString(),
    isActive: true,
    history: generateHistory(30, 0.45)
  },
  {
    id: nanoid(),
    name: 'Blue Light Control',
    targetDays: 7,
    currentStreak: 4,
    bestStreak: 8,
    completedDays: 18,
    totalDays: 30,
    category: 'circadian',
    createdAt: '2024-01-01T00:00:00.000Z',
    lastUpdated: new Date().toISOString(),
    lastSaved: new Date().toISOString(),
    isActive: true,
    reminderTime: '19:00',
    history: generateHistory(30, 0.6)
  },
  {
    id: nanoid(),
    name: 'Protein Goal',
    targetDays: 7,
    currentStreak: 5,
    bestStreak: 13,
    completedDays: 22,
    totalDays: 30,
    category: 'nutrition',
    createdAt: '2024-01-01T00:00:00.000Z',
    lastUpdated: new Date().toISOString(),
    lastSaved: new Date().toISOString(),
    isActive: true,
    history: generateHistory(30, 0.7)
  }
];

export const useHabitStore = create<HabitState>()(
  persist(
    (set, get) => ({
      habits: initialHabits,

      addHabit: (habit) => set((state) => ({
        habits: [
          ...state.habits,
          {
            ...habit,
            id: nanoid(),
            createdAt: new Date().toISOString(),
            lastUpdated: new Date().toISOString(),
            lastSaved: new Date().toISOString(),
            history: [],
          },
        ],
      })),

      updateHabit: (id, updates) => set((state) => ({
        habits: state.habits.map((habit) =>
          habit.id === id
            ? {
                ...habit,
                ...updates,
                lastUpdated: new Date().toISOString(),
              }
            : habit
        ),
      })),

      toggleHabitCompletion: (id, date) => set((state) => {
        const habit = state.habits.find((h) => h.id === id);
        if (!habit) return state;

        const isCompleted = habit.history.some(
          (h) => h.date === date && h.completed
        );

        const newHistory = isCompleted
          ? habit.history.filter((h) => h.date !== date)
          : [...habit.history, { date, completed: true }];

        // Calculate new streak
        const sortedHistory = [...newHistory].sort((a, b) =>
          b.date.localeCompare(a.date)
        );
        let streak = 0;
        for (const entry of sortedHistory) {
          if (entry.completed) streak++;
          else break;
        }

        return {
          habits: state.habits.map((h) =>
            h.id === id
              ? {
                  ...h,
                  history: newHistory,
                  currentStreak: streak,
                  bestStreak: Math.max(streak, h.bestStreak),
                  completedDays: newHistory.filter((h) => h.completed).length,
                  lastUpdated: new Date().toISOString(),
                  lastCompleted: date,
                }
              : h
          ),
        };
      }),

      toggleHabitActive: (id) => set((state) => ({
        habits: state.habits.map((habit) =>
          habit.id === id
            ? { ...habit, isActive: !habit.isActive }
            : habit
        ),
      })),

      saveHabits: async () => {
        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 1000));
        set((state) => ({
          habits: state.habits.map((habit) => ({
            ...habit,
            lastSaved: new Date().toISOString(),
          })),
        }));
      },
    }),
    {
      name: 'habit-storage',
      version: 1,
    }
  )
);